/**
 * FAQ Page Component
 *
 * Frequently Asked Questions page with expandable sections
 */

import { useState } from "react";
import hillFooter from "../assets/svg/hillFooter.svg";

// FAQ data array
const faqData = [
  {
    question: "What is DuneWorks AI and how does it help engineers?",
    answer:
      "DuneWorks AI is your Autonomous Engineering Twin. It continuously understands what you're working on by analyzing whatever appears on your screen — technical output, errors, behaviors, patterns, system responses, or unexpected changes. With Autonomous Mode, it observes in real time, detects important signals, and surfaces meaningful insights without you doing anything. It feels like having a high-level engineering partner beside you — fast, proactive, hands-free, and always available to guide your next step.",
  },
  {
    question: "Do I need to connect integrations like Jira or PagerDuty?",
    answer:
      "No. DuneWorks AI works immediately without integrations. It understands the content already visible on your screen and gives guidance instantly. Integrations are optional and only add deeper context or workflow automation, not a requirement.",
  },
  {
    question: "How secure is my data?",
    answer:
      "Your data never leaves your browser unless you upload something manually. All analysis is ephemeral, read-only, and compliant with GDPR & CCPA. DuneWorks AI cannot read passwords, private keys, or secure inputs.",
  },
  {
    question: "Can DuneWorks AI take autonomous actions on my behalf?",
    answer:
      "No. Even though Autonomous Mode analyzes your screen in real time, it never performs actions or changes anything in your tools. It only observes, interprets, and guides — all control stays with you.",
  },
  {
    question: "What can I ask the DuneWorks AI chat?",
    answer: `Anything an engineer might ask while trying to understand or debug something, such as:
• "Explain what's happening here."
• "Summarize this."
• "What should I check first?"
• "Guide me through this issue."
• "Why is this happening?"
• "Help me understand this output."
• "Analyze this page."

The chat is a purpose-built engineering intelligence assistant, designed to interpret context and simplify complex information.`,
  },
  {
    question: "Why is DuneWorks not analyzing my page automatically?",
    answer:
      'Autonomous Mode may be off, or the page may not contain readable text. Enable Autonomous Mode or type: "Analyze this page."',
  },
  {
    question: "Why does the extension say 'No analyzable content found'?",
    answer:
      "Some dashboards render visuals as images or canvases instead of text. Upload a screenshot or scroll to reveal log text for better results.",
  },
  {
    question: "Can DuneWorks AI help with unfamiliar or unknown problems?",
    answer:
      "Yes — that's one of its strongest capabilities. DuneWorks AI can interpret any unfamiliar or confusing information on your screen, explain what it means, highlight possible causes, and guide your next steps. Whether the issue is in a tool, a system, a process, or a workflow, your Engineering Twin gives you clarity within seconds.",
  },
  {
    question: "How can I contact the support team?",
    answer:
      'You can reach us anytime at support@duneworks.ai. You can also use the "Send Feedback" or "Contact Support" buttons inside the extension.',
  },
];

export function FAQ() {
  const [openFAQ, setOpenFAQ] = useState<number | null>(null);

  return (
    <div className="w-full h-full flex flex-col bg-[#FAF6F1] dark:bg-[#1A2D47]">
      <div className="flex-1 overflow-y-auto">
        <div className="max-w-3xl mx-auto">
          {/* Header Section */}
          <div className="w-full flex flex-col gap-[6px] p-4">
            {/* Title */}
            <h1 className="text-[#0A1423] dark:text-[#E3E6EA] text-[20px] font-semibold leading-[140%]">
              Frequently Asked Questions
            </h1>

            {/* Subtitle */}
            <p className="text-[#8E8378] dark:text-[#9AAABF] text-[14px] font-light leading-[125%] mt-[2px]">
              Find quick answers to the most common questions about Dune AI.
            </p>

            {/* Divider Line */}
            <div className="w-full h-[1px] bg-[#E5DDD3] dark:bg-[#2C405F] mt-[8px]" />
          </div>

          {/* FAQ Questions Section (scrollable) */}
          <div className="px-4 pb-4 flex flex-col gap-2">
            {faqData.map((faq, index) => {
              const isOpen = openFAQ === index;

              return (
                <div
                  key={index}
                  className={`
                w-full
                rounded-[10px]
                p-[12px]
                flex flex-col
                transition-all duration-200
                ${
                  isOpen
                    ? "bg-[#FDFAF7] border border-[#E5DDD3] shadow-sm"
                    : "bg-[#F3ECE4]"
                }
              `}
                >
                  {/* Question + Arrow */}
                  <div
                    className="flex justify-between items-start cursor-pointer"
                    onClick={() => setOpenFAQ(isOpen ? null : index)}
                  >
                    {/* Question Text */}
                    <p className="text-[#1D1B19] text-[14px] font-medium leading-[125%]">
                      {faq.question}
                    </p>

                    {/* Arrow Icon */}
                    <svg
                      className={`w-[18px] h-[18px] ml-2 shrink-0 transition-transform duration-200 ${
                        isOpen ? "rotate-180" : ""
                      }`}
                      viewBox="0 0 18 18"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M4.5 6.75L9 11.25L13.5 6.75"
                        stroke="#1D1B19"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </div>

                  {/* Answer (only visible when open) */}
                  {isOpen && (
                    <p className="text-[#6C635B] text-[14px] font-light leading-[135%] mt-[8px] whitespace-pre-wrap">
                      {faq.answer}
                    </p>
                  )}
                </div>
              );
            })}
          </div>

          {/* Footer Section */}
          <div className="bg-[#F3ECE4] rounded-[10px] mx-[16px] mb-[16px] flex flex-col items-center">
            {/* Padded content (text + button) */}
            <div className="w-full flex flex-col items-center gap-[12px] pt-[20px] pb-[20px] px-[20px]">
              {/* Text Content */}
              <div className="w-full flex flex-col items-center text-center gap-[6px]">
                <p className="text-[#0A1423] text-[16px] font-semibold leading-[140%]">
                  Still need help?
                </p>
                <p className="text-[#8E8378] text-[14px] font-light leading-[125%]">
                  Our support team is always ready to assist.
                </p>
              </div>

              {/* Contact Button */}
              <a
                href="mailto:support@duneworks.ai"
                className="px-[16px] py-[8px] bg-[#E5DDD3] text-[#1D1B19] rounded-[6px] text-[12px] font-medium hover:bg-[#D5CDC1] transition-colors"
              >
                Contact Support
              </a>
            </div>

            {/* SVG — OVERLAP */}
            <div className="w-full overflow-hidden rounded-b-[10px] -mt-[30px]">
              <img src={hillFooter} alt="" className="w-full h-auto block" />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
